import type { CommandManager, ICommand } from '@model-framework/command';
import { CompositeCommand, UpdateCommand } from '@model-framework/command';
import { ObjectRepository } from '@framework/repository';
import type { DisplayOrderTree } from '@model-framework/display-order';
import { DisplayOrderDeleteCommand, DraggingElements } from '@model-framework/display-order';
import type { DisplayOrderRepository } from '@model-framework/display-order/infrastructure';
import type { LinkColor, LinkLineStyle, LinkMarkStyle } from '@model-framework/link';
import type { FontSize } from '@model-framework/text';
import {
  type AnnotationLinkId,
  type AnnotationLinkStyle,
  type DescriptionPanelId,
  type FileContainerId,
  type GroupId,
  type NodeId,
  type StickyZoneId,
  type UserId,
  type ViewModelAssetId,
  RTDBPath,
  ViewModelFileStorageLimitBytes,
} from 'balus-common';
import { UserKey } from '@user/domain';
import { SelectedItemsOperation } from '@view-model/adapter/SelectedItemsOperation';
import type {
  ApplicationClipboardPayload,
  ImageClipboardPayload,
  FileClipboardPayload,
  TSVClipboardPayload,
  TextClipboardPayload,
} from '@view-model/application/clipboard';
import {
  StickyModelClipboardPayload,
  isFileClipboardPayload,
  isImageClipboardPayload,
  isStickyModelClipboardPayload,
  isTSVClipboardPayload,
  isTextClipboardPayload,
} from '@view-model/application/clipboard';
import { ElementsMoveCommand } from '@view-model/command/basic-model/ElementsMoveCommand/ElementsMoveCommand';
import type { LinkKey, LinkableTargetKey, ModelElementId } from '@view-model/domain/key';
import { StickyZoneKey, NodeKey } from '@view-model/domain/key';
import type { StickyModel } from '@view-model/domain/model/StickyModel';
import { StickyModelContents } from '@view-model/domain/model/model-contents/StickyModelContents';
import {
  ModelElementPositionMap,
  StickyModelElementSelector,
  LinkableTargetCollection,
} from '@view-model/domain/model';
import type { ViewModelEntity } from '@view-model/domain/view-model';
import { ViewModelAssetCollectionForClone } from '@view-model/domain/view-model';
import { FileContainerAssetCloner } from '@view-model/domain/view-model/FileContainerAssetCloner';
import { FileContainerAssetTrashCommand } from '@view-model/models/sticky/FileContainer/command/FileContainerAssetTrashCommand';
import type { StickyModelElementPositionMapRepository } from '@view-model/infrastructure/basic-model/StickyModelElementPositionMapRepository';
import type { IPositionSetRepository, PositionSet } from '@view-model/models/common/PositionSet';
import { PositionSetRepository } from '@view-model/models/common/PositionSet/infrastructure/PositionSetRepository';
import type { SizeSet } from '@view-model/models/common/basic';
import { Point, SelectedItemSet, Size } from '@view-model/models/common/basic';
import { Rect } from '@view-model/models/common/basic/Rect';
import type { ThemeColor } from '@view-model/models/common/color';
import type { TextAlign } from '@view-model/models/common/text-align';
import type { Position, PositionDelta } from '@view-model/models/common/types/ui';
import { DescriptionPanel } from '@view-model/models/sticky/DescriptionPanel/domain/DescriptionPanel';
import { DescriptionPanelCollectionContents } from '@view-model/models/sticky/DescriptionPanel/domain/DescriptionPanelCollectionContents';
import { DescriptionPanelCreationOperation } from '@view-model/models/sticky/DescriptionPanel/adapter';
import { FileContainerCreationOperation } from '@view-model/models/sticky/FileContainer/adapter/FileContainerCreationOperation';
import type { CreatingModelComment } from '@view-model/models/sticky/ModelComment';
import { ModelCommentContents } from '@view-model/models/sticky/ModelComment/domain/ModelCommentContents';
import {
  ModelCommentOperation,
  ModelCommentThreadRepository,
  ModelCommentsCreateCommand,
} from '@view-model/models/sticky/ModelComment';
import type { LinkCollection } from '@view-model/models/sticky/StickyLink/domain/LinkCollection';
import type { LinkBend } from '@view-model/models/sticky/StickyLink/domain/vo/LinkBend';
import type { LinkEntity, LinkRepository, LinkStyleSet } from '@view-model/models/sticky/StickyLink';
import { LinkEntityOperation } from '@view-model/models/sticky/StickyLink/adapter/LinkEntityOperation';
import { LinkStyleUpdateMultiCommand, StickyLinkCreationOperation } from '@view-model/models/sticky/StickyLink';
import { LinkMultiDeleteCommand, LinkReverseCommand } from '@view-model/models/sticky/StickyLink/command';
import type { ElementDescriptionSet } from '@view-model/models/sticky/ElementDescription';
import type {
  NodeEntityOperation,
  NodeFontSize,
  NodeRepository,
  NodeStyleSet,
} from '@view-model/models/sticky/StickyNodeView';
import {
  NodeCollection,
  NodePosition,
  NodeStyleUpdateMultiCommand,
  StickyNode,
  StickyNodeCreationOperation,
} from '@view-model/models/sticky/StickyNodeView';
import type {
  StickyZoneCollection,
  StickyZoneEntityOperation,
  StickyZoneRepository,
  StickyZoneStyleSet,
  StickyZoneShape,
  StickyZoneTitlePosition,
} from '@view-model/models/sticky/StickyZoneView';
import {
  StickyZone,
  StickyZoneCreationOperation,
  StickyZoneStyleUpdateMultiCommand,
} from '@view-model/models/sticky/StickyZoneView';
import { ModelLayout } from '@view-model/models/sticky/layout';
import type { NewElementStyleProvider } from '@view-model/models/sticky/user-context';
import { FixedCellSizeTextTable, PastePositionAdjuster } from '@view-model/ui/components/Model';
import { StickyLinkReplacementOperation } from '../models/sticky/StickyLink/adapter';
import type { AlignType } from '@view-model/models/sticky/ui/MultiSelectionView/components';
import { NodeCollectionUpdateCommand } from '@view-model/models/sticky/StickyNodeView/command';
import { getDefaultStore } from 'jotai';
import type { StickyModelContentsState } from '@view-model/adapter/stickyModelContentsAtomFamily';
import { stickyModelContentsAtomFamily } from '@view-model/adapter/stickyModelContentsAtomFamily';
import { ClipboardUtils } from '@framework/ClipboardUtils';
import { StickyZoneChangeParentZoneCommand } from '../models/sticky/StickyZoneView/command/StickyZoneChangeParentZoneCommand';
import { t } from 'i18next';
import { toast } from 'react-hot-toast';
import {
  MaxFileAssetSizeInMB,
  ViewModelFileAssetUploader,
} from '@view-model/domain/view-model/ViewModelFileAssetUploader';
import { fitImageSize } from '@view-model/models/sticky/FileContainer/domain/FileContainerImageSize';
import { layoutFileContainers } from '@view-model/models/sticky/FileContainer/domain/FileContainerLayout';
import { newFileContainerId } from '@view-model/models/sticky/FileContainer/domain/FileContainerId';
import { FileContainerCollectionContents } from '@view-model/models/sticky/FileContainer/domain/FileContainerCollectionContents';
import { AnnotationLink } from '@view-model/models/sticky/AnnotationLink/domain/AnnotationLink';
import { AnnotationLinkCollection } from '@view-model/models/sticky/AnnotationLink/domain/AnnotationLinkCollection';
import type { AnnotationLinkOrigin } from '@view-model/models/sticky/AnnotationLink/domain/AnnotationLinkOrigin';
import {
  type UploadingFileContainer,
  uploadingFileContainersAtomFamily,
} from '@view-model/models/sticky/FileContainer/adapter/uploadingFileContainersAtom';

const MaxPasteTSVCount = 100;
const MaxUploadFileCount = 10;
const MaxPasteTextLength = 100;

type SelectedIdSet = SelectedItemSet<ModelElementId>;

export class StickyModelContentsOperation {
  // 内部に状態を持つのを避けたいが一旦ここに選択状態を持たせる
  private selectedItems: SelectedIdSet = SelectedItemSet.from([]);

  private readonly store = getDefaultStore();
  private readonly modelCommentOperation: ModelCommentOperation;
  private readonly modelCommentThreadRepository: ModelCommentThreadRepository;
  private readonly commentPositionSetRepository: IPositionSetRepository;
  private readonly descriptionPanelPositionSetRepository: IPositionSetRepository;
  private readonly fileContainerPositionSetRepository: IPositionSetRepository;
  private readonly selectedItemsOperation: SelectedItemsOperation;
  private readonly linkCreationOperation: StickyLinkCreationOperation;
  private readonly linkReplacementOperation: StickyLinkReplacementOperation;
  private readonly zoneCreationOperation: StickyZoneCreationOperation;
  private readonly pastePositionAdjuster: PastePositionAdjuster;
  private readonly nodeCreationOperation: StickyNodeCreationOperation;

  constructor(
    private readonly groupId: GroupId,
    private readonly viewModel: ViewModelEntity,
    private readonly model: StickyModel,
    private readonly currentUserId: UserId,
    private readonly nodeRepository: NodeRepository,
    private readonly linkRepository: LinkRepository,
    private readonly zoneRepository: StickyZoneRepository,
    private readonly commandManager: CommandManager,
    private readonly nodeEntityOperation: NodeEntityOperation,
    private readonly linkEntityOperation: LinkEntityOperation,
    private readonly zoneEntityOperation: StickyZoneEntityOperation,
    private readonly positionMapRepository: StickyModelElementPositionMapRepository,
    private readonly displayOrderRepository: DisplayOrderRepository,
    private readonly styleProvider: NewElementStyleProvider
  ) {
    this.selectedItemsOperation = new SelectedItemsOperation(this.viewModel.id, model.id);
    this.linkCreationOperation = new StickyLinkCreationOperation(
      this.viewModel,
      this.linkEntityOperation,
      this.commandManager
    );
    this.linkReplacementOperation = new StickyLinkReplacementOperation(
      this.viewModel,
      this.linkEntityOperation,
      this.commandManager
    );
    this.zoneCreationOperation = new StickyZoneCreationOperation(
      this.viewModel.id,
      this.model,
      this.currentUserId,
      this.zoneEntityOperation,
      this.commandManager,
      this.displayOrderRepository
    );

    this.modelCommentThreadRepository = new ModelCommentThreadRepository(this.viewModel.id, this.model.id);

    this.commentPositionSetRepository = new PositionSetRepository(
      RTDBPath.Comment.positionsPath(this.viewModel.id, this.model.id)
    );

    this.modelCommentOperation = new ModelCommentOperation(
      this.modelCommentThreadRepository,
      this.commentPositionSetRepository,
      this.commandManager
    );

    this.descriptionPanelPositionSetRepository = new PositionSetRepository(
      RTDBPath.DescriptionPanel.positionsPath(this.viewModel.id, this.model.id)
    );

    this.fileContainerPositionSetRepository = new PositionSetRepository(
      RTDBPath.FileContainer.positionsPath(this.viewModel.id, this.model.id)
    );

    this.nodeCreationOperation = new StickyNodeCreationOperation(
      this.currentUserId,
      this.nodeEntityOperation,
      this.commandManager,
      this.displayOrderRepository
    );

    const { width: nodeWidth, height: nodeHeight } = StickyNode.size();
    this.pastePositionAdjuster = new PastePositionAdjuster(nodeWidth / 2, nodeHeight / 2);
  }

  deleteMySelection(): void {
    this.selectedItemsOperation.deleteMySelection();
    this.selectedItems = SelectedItemSet.from([]);
  }

  /**
   * 指定の要素間を接続するリンクを作成する
   * @param fromKey
   * @param toKey
   */
  createLink(fromKey: LinkableTargetKey, toKey: LinkableTargetKey): LinkKey | undefined {
    return this.linkCreationOperation.createLink(fromKey, toKey, this.styleProvider.linkStyle());
  }

  createAnnotationLink(from: AnnotationLinkOrigin, to: LinkableTargetKey): AnnotationLinkId {
    const link = AnnotationLink.buildNew(from, to).withStyle(this.styleProvider.annotationLinkStyle());
    this.commandManager.execute(link.buildCreateCommand(this.viewModel.id, this.model.id));
    return link.id;
  }

  findFileContainerIdByPosition(position: Position): FileContainerId | null {
    const { fileContainerContents } = this;
    const topmostFirst = fileContainerContents.allIds().reverse();
    return topmostFirst.find((id) => fileContainerContents.getRect(id)?.includePosition(position)) ?? null;
  }

  findDescriptionPanelIdByPosition(position: Position): DescriptionPanelId | null {
    return this.panelContents.filterByPositionSelection(position).allIds().at(-1) ?? null;
  }

  changeAnnotationLinkStyle(id: AnnotationLinkId, style: AnnotationLinkStyle): void {
    const link = this.annotationLinks.get(id);
    if (!link) return;

    this.styleProvider.annotationLinkStyleChanged(style);
    if (link.style !== style) this.updateAnnotationLink(link, link.withStyle(style));
  }

  changeAnnotationLinkBend(id: AnnotationLinkId, bend: LinkBend): void {
    const link = this.annotationLinks.get(id);
    if (!link || link.bend.isEquals(bend)) return;

    this.updateAnnotationLink(link, link.withBend(bend));
  }

  reverseAnnotationLink(id: AnnotationLinkId): void {
    const link = this.annotationLinks.get(id);
    if (!link) return;

    this.updateAnnotationLink(link, link.reverse());
  }

  private updateAnnotationLink(link: AnnotationLink, updated: AnnotationLink): void {
    this.commandManager.execute(
      new UpdateCommand(
        link,
        updated,
        new ObjectRepository(
          AnnotationLink,
          RTDBPath.AnnotationLink.linkPath(this.viewModel.id, this.model.id, link.id)
        )
      )
    );
  }

  replaceLink(
    linkKey: LinkKey,
    currentKeys: { sourceKey: LinkableTargetKey; targetKey: LinkableTargetKey },
    newKeys: { sourceKey: LinkableTargetKey; targetKey: LinkableTargetKey }
  ): void {
    return this.linkReplacementOperation.replaceLink(linkKey, currentKeys, newKeys);
  }

  buildUpdateParentRelationsForMovedZoneCommand(): ICommand | undefined {
    // 単一ゾーン以外が選択されてる場合は除外
    const zoneCount = this.selectedZones().ids().length;
    const minimizedElementCount = this.displayOrderTree.minimizeElementIds(
      this.selectedItems.getSelectedItems()
    ).length;
    if (!this.isSingleZoneSelection(zoneCount, minimizedElementCount)) return;

    const droppingContents = this.buildSelectedContents({ includeZoneContents: true }).contents;
    const droppingZones = droppingContents.zoneEntities();
    const droppingZonePositions = droppingContents.zonePositionSet();
    if (droppingZones.length === 0 || droppingZonePositions.isEmpty()) return;

    const droppingRect = this.getDroppingRect();
    if (!droppingRect) return;

    const changeParentZoneMap = this.buildChangeParentZoneMap(
      droppingZones,
      droppingZonePositions,
      droppingRect,
      (zoneIds) => this.displayOrderTree.findLowestDescendantZoneId(zoneIds)
    );

    if (Object.keys(changeParentZoneMap).length === 0) return;

    return new CompositeCommand(
      ...Object.entries(changeParentZoneMap).map(
        ([parentZoneId, childElementIds]) =>
          new StickyZoneChangeParentZoneCommand(childElementIds, [parentZoneId], this.displayOrderRepository)
      )
    );
  }

  /**
   * ドロップ対象の要素に対する親ゾーンの変更マップを構築する
   * @param droppingZones ドロップするゾーンのリスト
   * @param droppingZonePositions ドロップするゾーンの位置情報
   * @param droppingRect ドロップする要素群の矩形範囲
   * @param findLowestDescendantZoneId 候補となるゾーンIDから最も子孫のゾーンIDを探す関数
   */
  private buildChangeParentZoneMap(
    droppingZones: StickyZone[],
    droppingZonePositions: PositionSet,
    droppingRect: Rect,
    findLowestDescendantZoneId: (zoneIds: StickyZoneId[]) => StickyZoneId | null
  ): Record<StickyZoneId, (StickyZoneId | NodeId)[]> {
    // ドロップ先にある要素群のうち、最もトップにある要素のみを抽出
    const minimizedDropTargetElementIds = this.displayOrderTree
      .minimizeElementIds(this.findElementsIncludedByDroppingRect(droppingRect))
      .filter((id) => !droppingZones.some((zone) => zone.id === id));

    const changeParentZoneMap: Record<StickyZoneId, (StickyZoneId | NodeId)[]> = {};

    minimizedDropTargetElementIds.forEach((dropTargetElementId) => {
      const dropTargetElement =
        this.model.findNode(NodeKey.buildFromID(dropTargetElementId)) ||
        this.model.findZone(StickyZoneKey.buildFromID(dropTargetElementId));
      if (!dropTargetElement) return;

      const dropTargetElementRect = (() => {
        if (dropTargetElement instanceof StickyZone) {
          const dropTargetElementPosition = this.model.zonePositions.find(dropTargetElementId);
          return dropTargetElementPosition ? dropTargetElement.getRect(dropTargetElementPosition) : null;
        }
        return dropTargetElement.getRect();
      })();
      if (!dropTargetElementRect) return;

      const parentCandidateZoneIds = droppingZones
        .filter((zone) => {
          const parentCandidateZonePosition = droppingZonePositions.find(zone.id);
          if (!parentCandidateZonePosition) return false;

          const parentCandidateZoneRect = zone.getRect(parentCandidateZonePosition);
          if (!parentCandidateZoneRect) return false;

          return parentCandidateZoneRect.includeRect(dropTargetElementRect);
        })
        .map((zone) => zone.id);

      // 移動したゾーン内のさらに子孫ゾーンが要素を飲み込む場合もあるので、その場合は最も深い子孫ゾーンのみが親になるようにしている
      const lowestDescendantZoneId = findLowestDescendantZoneId(parentCandidateZoneIds);
      if (!lowestDescendantZoneId) return;

      if (!changeParentZoneMap[lowestDescendantZoneId]) {
        changeParentZoneMap[lowestDescendantZoneId] = [];
      }
      changeParentZoneMap[lowestDescendantZoneId].push(dropTargetElementId);
    });

    return changeParentZoneMap;
  }

  buildCreateElementsCommandFromOtherModel(contents: StickyModelContents, parentZoneIds?: StickyZoneId[]): ICommand {
    const createElementsCommand = this.buildCreateElementsCommand(contents, { parentZoneIds });

    // 移動した単一ゾーンのみで、かつ他の要素を飲み込む場合は、親子関係の更新を行う
    const zoneCount = contents.zoneEntities().length;
    const minimizedElementCount = contents.minimizedElementIds().length;
    if (!this.isSingleZoneSelection(zoneCount, minimizedElementCount)) return createElementsCommand;

    const droppingZones = contents.zoneEntities();
    const droppingZonePositions = contents.zonePositionSet();
    if (droppingZones.length === 0 || droppingZonePositions.isEmpty()) return createElementsCommand;

    const droppingRect = contents.getUnionRect();
    if (!droppingRect) return createElementsCommand;

    const changeParentZoneMap = this.buildChangeParentZoneMap(
      droppingZones,
      droppingZonePositions,
      droppingRect,
      (zoneIds) => contents.findLowestDescendantZoneId(zoneIds)
    );

    if (Object.keys(changeParentZoneMap).length === 0) return createElementsCommand;

    return new CompositeCommand(
      createElementsCommand,
      ...Object.entries(changeParentZoneMap).map(
        ([parentZoneId, childElementIds]) =>
          new StickyZoneChangeParentZoneCommand(childElementIds, [parentZoneId], this.displayOrderRepository)
      )
    );
  }

  private isSingleZoneSelection(zoneCount: number, minimizedElementCount: number): boolean {
    return zoneCount > 0 && minimizedElementCount === 1;
  }

  private buildCreateElementsCommand(
    modelContents: StickyModelContents,
    options: {
      parentZoneIds?: StickyZoneId[];
    } = {}
  ): ICommand {
    const elementsCreateCommand = modelContents.buildElementsCreateCommand(
      this.viewModel,
      this.model,
      this.nodeEntityOperation,
      this.linkEntityOperation,
      this.zoneEntityOperation,
      this.displayOrderRepository,
      options.parentZoneIds
    );

    const commentCreateCommand = new ModelCommentsCreateCommand(
      modelContents.commentContents(),
      this.modelCommentThreadRepository,
      this.commentPositionSetRepository
    );

    const descriptionPanelsCreateCommand = modelContents.buildDescriptionPanelsCreateCommand(
      this.viewModel.id,
      this.model.id
    );

    const fileContainersCreateCommand = modelContents.buildFileContainersCreateCommand(
      this.viewModel.id,
      this.model.id
    );

    const annotationLinksCreateCommand = modelContents.buildAnnotationLinksCreateCommand(
      this.viewModel.id,
      this.model.id
    );

    return new CompositeCommand(
      elementsCreateCommand,
      commentCreateCommand,
      descriptionPanelsCreateCommand,
      fileContainersCreateCommand,
      annotationLinksCreateCommand
    );
  }

  private createElements(modelContents: StickyModelContents, parentZoneIds?: StickyZoneId[]): void {
    const command = this.buildCreateElementsCommand(modelContents, { parentZoneIds });

    FileContainerAssetTrashCommand.executeCreation(
      this.commandManager,
      { groupId: this.groupId, viewModelId: this.viewModel.id },
      modelContents.fileContainerAssetIds(),
      command
    );
  }

  async deleteSelectedElements(): Promise<boolean> {
    const fileContainers = this.selectedFileContainers();
    const command = await this.buildDeleteSelectedElementsCommand({ fileContainers });
    if (!command) return false;

    const deleted = await this.executeWithFileAssetTrash(fileContainers.assetIds(), command);
    if (deleted) this.deselectAll();
    return deleted;
  }

  async buildDeleteSelectedElementsCommand(
    options: {
      includeZoneContents?: boolean;
      fileContainers?: FileContainerCollectionContents;
    } = {}
  ): Promise<ICommand | null> {
    const fileContainers = options.fileContainers ?? this.selectedFileContainers();
    const descriptionPanels = this.selectedDescriptionPanel();
    const annotationLinks = this.selectedAnnotationLinks();
    const zoneElementIds = options.includeZoneContents
      ? this.displayOrderTree.aggregateDescendantIds(this.selectedZones().ids())
      : [];

    const nodes = this.selectedNodes().addCollection(this.model.getNodes(zoneElementIds));
    const zones = this.selectedZones().addZones(this.model.getZones(zoneElementIds).entities());

    const commentPositionSet = await this.commentPositionSetRepository.getMany(this.selectedComments().allIds());
    const commentDeleteCommand = await this.selectedComments().buildDeleteCommand(
      this.modelCommentThreadRepository,
      this.commentPositionSetRepository,
      commentPositionSet
    );

    const descriptionPanelPositionSet = await this.descriptionPanelPositionSetRepository.getMany(
      descriptionPanels.allIds()
    );
    const descriptionPanelDeleteCommand = await descriptionPanels.buildDeleteCommand(
      this.viewModel.id,
      this.model.id,
      descriptionPanelPositionSet
    );

    const fileContainerDeleteCommand = await this.buildFileContainersDeleteCommand(fileContainers);

    const nodesDeleteCommand = await nodes.buildDeleteCommand(this.nodeRepository);
    const zoneDeleteCommand = await zones.buildDeleteCommand(this.viewModel.id, this.model.id);

    const displayOrderDeleteCommand = DisplayOrderDeleteCommand.build(
      nodes.ids(),
      zones.ids(),
      this.displayOrderRepository
    );

    const linkDeleteCommand = this.selectedLinks()
      .addList(LinkEntityOperation.findLinksByNodeKeys(nodes.keys(), this.viewModel))
      .addList(LinkEntityOperation.findLinksByZoneKeys(zones.keys(), this.viewModel))
      .buildDeleteCommand(this.viewModel, this.linkEntityOperation);

    const annotationLinkDeleteCommand = await annotationLinks
      .merge(
        this.annotationLinks.filterConnectedTo(
          [...fileContainers.allIds(), ...descriptionPanels.allIds()],
          [...nodes.keys(), ...zones.keys()]
        )
      )
      .buildDeleteCommand(this.viewModel.id, this.model.id);

    return CompositeCommand.composeOptionalCommands(
      nodesDeleteCommand,
      zoneDeleteCommand,
      linkDeleteCommand,
      displayOrderDeleteCommand,
      commentDeleteCommand,
      descriptionPanelDeleteCommand,
      fileContainerDeleteCommand,
      annotationLinkDeleteCommand
    );
  }

  /**
   * 適切な位置にゾーンを追加し、そのゾーンを選択する
   *
   *  * 選択されている要素があれば、それの外接領域の左上からオフセットした位置に作成
   *  * 選択要素がなければ、ビューの中のランダムな位置に作成
   */
  createZone(position: Point): StickyZoneId {
    const zoneId = this.zoneCreationOperation.createZone(position, this.styleProvider.zoneStyle());
    this.selectOnly(zoneId);

    return zoneId;
  }

  /**
   * 指定された色で指定された位置にゾーンを作成します。
   * 次回の新規作成にも同じ色が反映されます。
   * @param position
   * @param color
   */
  createZoneWithColor(position: Point, color: ThemeColor): StickyZoneId {
    this.styleProvider.zoneColorChanged(color);
    return this.createZone(position);
  }

  /**
   * クリックした位置に指定された色のゾーンを作成します。
   * @param clickedPosition
   * @param color
   */
  createZoneAtClickedPositionWithColor(clickedPosition: Point, color: ThemeColor): StickyZoneId {
    const size = StickyZone.size();
    const position = clickedPosition.addXY(-size.width / 2, -size.height / 2);
    return this.createZoneWithColor(position, color);
  }

  private selectedComments(): ModelCommentContents {
    return this.commentContents.filterByIds(this.selectedItems.getSelectedItems());
  }

  private selectedDescriptionPanel(): DescriptionPanelCollectionContents {
    return this.panelContents.filterByIds(this.selectedItems.getSelectedItems());
  }

  private get panelContents(): DescriptionPanelCollectionContents {
    return this.getStickyModelContentsState().panelContents;
  }

  private selectedFileContainers(): FileContainerCollectionContents {
    return this.fileContainerContents.filterByIds(this.selectedItems.getSelectedItems());
  }

  private get fileContainerContents(): FileContainerCollectionContents {
    return this.getStickyModelContentsState().fileContainerContents;
  }

  private selectedAnnotationLinks(): AnnotationLinkCollection {
    return this.annotationLinks.filterByIds(this.selectedItems.getSelectedItems());
  }

  private get annotationLinks(): AnnotationLinkCollection {
    return this.getStickyModelContentsState().annotationLinks;
  }

  private get commentContents(): ModelCommentContents {
    return this.getStickyModelContentsState().commentContents;
  }

  private get nodeDescriptionSet(): ElementDescriptionSet {
    return this.getStickyModelContentsState().nodeDescriptionSet;
  }

  private get zoneDescriptionSet(): ElementDescriptionSet {
    return this.getStickyModelContentsState().zoneDescriptionSet;
  }

  private getStickyModelContentsState(): StickyModelContentsState {
    return this.store.get(stickyModelContentsAtomFamily(this.model.id));
  }

  private get displayOrderTree(): DisplayOrderTree {
    return this.getStickyModelContentsState().displayOrderTree;
  }

  private buildMoveNodeZoneCommand(dx: number, dy: number): ICommand | undefined {
    const positions = this.selectedModelElementPositionMap();

    if (positions.isEmpty()) return;

    return new ElementsMoveCommand(positions, positions.movePosition(dx, dy), this.positionMapRepository);
  }

  /**
   * 選択済みの要素を dx, dy だけ移動させる
   *
   * @param dx
   * @param dy
   */
  moveSelectedElements(dx: number, dy: number): void {
    const elementsMoveCommand = this.buildMoveNodeZoneCommand(dx, dy);
    const modelCommentCommand = this.selectedComments().buildMoveCommand(dx, dy, this.commentPositionSetRepository);
    const descriptionPanelsMoveCommand = this.selectedDescriptionPanel().buildMoveCommand(
      dx,
      dy,
      this.descriptionPanelPositionSetRepository
    );
    const fileContainersMoveCommand = this.selectedFileContainers().buildMoveCommand(
      dx,
      dy,
      this.fileContainerPositionSetRepository
    );

    const command = CompositeCommand.composeOptionalCommands(
      elementsMoveCommand,
      modelCommentCommand,
      descriptionPanelsMoveCommand,
      fileContainersMoveCommand
    );

    if (command) {
      this.commandManager.execute(command);
    }
  }

  /**
   * 矩形領域に交差する/含まれるモデル要素を選択する
   */
  selectElementsByRect({
    selectionRect,
    zoneId,
    commentSizeSet,
  }: {
    selectionRect: Rect;
    zoneId?: StickyZoneId;
    commentSizeSet: SizeSet;
  }): void {
    const { displayOrderTree } = this;
    let selectedIds = this.getElementIdsByRect({
      rect: selectionRect,
      panelContents: this.panelContents,
      fileContainerContents: this.fileContainerContents,
      commentSizeSet,
    });
    if (zoneId) {
      // ゾーンの領域内から矩形領域の選択を開始したときにはそのゾーンを選択対象から除外する
      // ref: https://docs.google.com/presentation/d/1HPSNsBQj5gsRvoLocKoOSyx2Uc-TGDJDU6XWiwXgtKs/edit
      const removedZoneIds = displayOrderTree.ancestors(zoneId);
      removedZoneIds.push(zoneId);
      selectedIds = selectedIds.filter((id) => !removedZoneIds.includes(id));
    }
    this.addToSelectionMany(selectedIds);
  }

  /**
   * ドラッグして拡縮ゾーンの矩形領域に新たに含まれる/外れる Node と Zone の Id 一覧を返す
   */
  getChangeParentElementIdsByNewZoneRect(
    selectionRect: Rect,
    zoneId: StickyZoneId
  ): {
    removedElementIds: (NodeId | StickyZoneId)[];
    addedElementIds: (NodeId | StickyZoneId)[];
    ancestorFlipInfo?: {
      ancestorId: StickyZoneId;
      ancestorParentZoneIds: StickyZoneId[];
      targetParentZoneIds: StickyZoneId[];
      targetChildElementIds: (NodeId | StickyZoneId)[];
    };
  } {
    const allNodes = this.model.nodeEntities();
    const allNodePositions = new NodeCollection(allNodes).positionSet();
    const allLinks = this.model.linkEntities();
    const allZones = this.model.zones.models;
    const allZonePositions = this.model.zonePositions;
    const shapeMap = this.model.nodeZoneShapeMap();

    const selector = new StickyModelElementSelector(
      allNodes,
      allNodePositions,
      allLinks,
      allZones,
      allZonePositions,
      shapeMap
    );

    const { nodes, zones } = selector.selectByRect(selectionRect);

    // includeRect の判定に必要なので、elements の rect と id のペアを作成
    const nodeRects = nodes.map((node) => ({
      id: node.id,
      rect: new Rect(node.position, node.size()),
    }));
    const zoneRects = zones.map((zone) => ({
      id: zone.id,
      rect: new Rect(allZonePositions.find(zone.id) as Point, zone.size),
    }));

    // selectByRect で取得した elements は矩形と交差しているものをすべて取り出すので、厳密に矩形に含まれるようになる elements だけに絞り込む
    const nodesIncludedByRect = nodeRects.filter((nodeRect) => selectionRect.includeRect(nodeRect.rect));
    const zonesIncludedByRect = zoneRects.filter((zoneRect) => selectionRect.includeRect(zoneRect.rect));
    const ancestorFlipInfo = this.findAncestorFlipInfo(
      zonesIncludedByRect.map((zoneRect) => zoneRect.id),
      zoneId
    );

    let selectedElementIds = [
      ...nodesIncludedByRect.map((node) => node.id),
      ...zonesIncludedByRect.map((zone) => zone.id),
    ];

    // 新しいゾーンの矩形から外れた element の Id
    const removedElementIds = this.displayOrderTree
      .addZoneDescendantIds([zoneId])
      .filter((id) => !selectedElementIds.includes(id))
      // 親として指定ゾーン以外を持つ elements を除去
      .filter((id) => this.displayOrderTree.ancestors(id).filter((id) => id !== zoneId).length === 0);

    // 親を持たない elements だけを取り出す
    selectedElementIds = selectedElementIds.filter((id) => !this.displayOrderTree.ancestors(id).length);
    // 新しいゾーンの矩形に追加された element の Id
    const addedElementIds = selectedElementIds.filter((id) => id != zoneId);

    return { removedElementIds, addedElementIds, ancestorFlipInfo };
  }

  private findAncestorFlipInfo(
    fullyIncludedZoneIds: StickyZoneId[],
    targetZoneId: StickyZoneId
  ):
    | {
        ancestorId: StickyZoneId;
        ancestorParentZoneIds: StickyZoneId[];
        targetParentZoneIds: StickyZoneId[];
        targetChildElementIds: (NodeId | StickyZoneId)[];
      }
    | undefined {
    const ancestorCandidates = fullyIncludedZoneIds.filter(
      (zoneId) => zoneId !== targetZoneId && this.displayOrderTree.hasZoneWithDescendant(zoneId, targetZoneId)
    );

    if (!ancestorCandidates.length) return undefined;

    const targetParentZoneIds = this.displayOrderTree.findParentZoneIdsOf(targetZoneId);
    const targetChildElementIds = this.getDirectChildElementIds(targetZoneId);

    const outermostAncestorId =
      ancestorCandidates.find(
        (candidateId) =>
          !ancestorCandidates.some(
            (otherId) => otherId !== candidateId && this.displayOrderTree.hasZoneWithDescendant(otherId, candidateId)
          )
      ) || ancestorCandidates[0];

    const ancestorParentZoneIds = this.displayOrderTree
      .findParentZoneIdsOf(outermostAncestorId)
      .filter((parentId) => parentId !== targetZoneId);

    return {
      ancestorId: outermostAncestorId,
      ancestorParentZoneIds,
      targetParentZoneIds,
      targetChildElementIds,
    };
  }

  private getDirectChildElementIds(zoneId: StickyZoneId): (NodeId | StickyZoneId)[] {
    const childIds: (NodeId | StickyZoneId)[] = [];

    const nodeEntities = this.model.nodeEntities();
    nodeEntities.forEach((node) => {
      const parentIds = this.displayOrderTree.findParentZoneIdsOf(node.id);
      if (parentIds.includes(zoneId)) {
        childIds.push(node.id);
      }
    });

    const zoneEntities = this.model.zones.models;
    zoneEntities.forEach((zone) => {
      if (zone.id === zoneId) return;
      const parentIds = this.displayOrderTree.findParentZoneIdsOf(zone.id);
      if (parentIds.includes(zoneId)) {
        childIds.push(zone.id);
      }
    });

    return childIds;
  }

  /**
   * 矩形領域に交差する/含まれるモデル要素の Id 一覧を返す
   */
  private getElementIdsByRect({
    rect,
    panelContents,
    fileContainerContents,
    commentSizeSet,
  }: {
    rect: Rect;
    panelContents: DescriptionPanelCollectionContents;
    fileContainerContents: FileContainerCollectionContents;
    commentSizeSet: SizeSet;
  }): ModelElementId[] {
    const nodeZoneLinkIds = this.selectIdsByRect(rect);
    const panelIds = panelContents.filterByRectSelection(rect).allIds();
    const fileContainerIds = fileContainerContents.filterByRectSelection(rect).allIds();
    return [
      ...nodeZoneLinkIds,
      ...ModelCommentOperation.getModelCommentThreadIdsByRect({
        rect,
        commentContents: this.commentContents,
        commentSizeSet,
      }),
      ...panelIds,
      ...fileContainerIds,
      ...this.annotationLinks
        .validBy(
          [...fileContainerIds, ...panelIds],
          [...this.model.getNodes(nodeZoneLinkIds).keys(), ...this.model.getZones(nodeZoneLinkIds).keys()]
        )
        .allIds(),
    ];
  }

  private selectIdsByRect(rect: Rect): ModelElementId[] {
    const nodes = this.model.nodeEntities();
    const nodePositions = new NodeCollection(nodes).positionSet();
    const links = this.model.linkEntities();
    const zones = this.model.zones.models;
    const zonePositions = this.model.zonePositions;
    const shapeMap = this.model.nodeZoneShapeMap();

    const selector = new StickyModelElementSelector(nodes, nodePositions, links, zones, zonePositions, shapeMap);
    return selector.selectIdsByRect(rect);
  }

  /**
   * 選択中の要素から構成される StickyModelContents を返します。
   *
   * モデルとして不整合になるようなリンク（例えばリンクだけ選択されている場合のリンク）はStickyModelContentsから
   * 除外され、別途コレクションとして返されます。
   */
  private buildSelectedContents(
    options: {
      includeZoneContents?: boolean;
    } = {}
  ): { contents: StickyModelContents; excludedLinks: LinkCollection } {
    const { model, displayOrderTree } = this;

    const otherZoneIds = options.includeZoneContents
      ? displayOrderTree.aggregateDescendantZoneIds(this.selectedZones().ids())
      : [];

    const otherElementIds = options.includeZoneContents
      ? displayOrderTree.aggregateDescendantIds(this.selectedZones().ids())
      : [];

    const nodes = this.selectedNodes().addCollection(this.model.getNodes(otherElementIds));
    const zones = this.selectedZones().addZones(this.model.getZones(otherZoneIds).entities());
    const zonePositions = this.selectedZonePositions().merge(this.model.getZonePositions(otherZoneIds));
    const nodeDescriptions = options.includeZoneContents
      ? this.selectedNodeDescriptions().merge(this.nodeDescriptionSet.filterByModelElementIds(nodes.ids()))
      : this.selectedNodeDescriptions();
    const zoneDescriptions = options.includeZoneContents
      ? this.selectedZoneDescriptions().merge(this.zoneDescriptionSet.filterByModelElementIds(zones.ids()))
      : this.selectedZoneDescriptions();

    const linkableTargetCollection = new LinkableTargetCollection(nodes.entities(), zones.entities());
    const links = options.includeZoneContents
      ? this.selectedLinks().addList(this.model.getAllLinks().validLinksBy(linkableTargetCollection)?.entities() ?? [])
      : this.selectedLinks();

    return StickyModelContents.fromFragments(
      model.key,
      model.version,
      nodes,
      links,
      zones,
      zonePositions,
      this.selectedComments(),
      this.selectedDescriptionPanel(),
      this.selectedFileContainers(),
      options.includeZoneContents ? this.annotationLinks : this.selectedAnnotationLinks(),
      nodeDescriptions,
      zoneDescriptions,
      displayOrderTree
    );
  }

  /**
   * 選択中の要素のID配列を返します
   */
  getSelectedIds(): ModelElementId[] {
    return this.selectedItems.getSelectedItems();
  }

  /**
   * 選択中の要素があるかどうかを返します。
   */
  hasSelectedElements(): boolean {
    return this.selectedItems.getSelectedItems().length > 0;
  }

  /**
   * 指定した位置に存在するモデル要素のうち一番手前にあるものを返す
   *
   * @param targetPosition
   */
  findForegroundElementByPosition(targetPosition: Position): StickyNode | StickyZone | null {
    return this.model.findForegroundElementByPosition(Point.fromPosition(targetPosition), this.displayOrderTree);
  }

  /**
   * クリックした位置にノードを作成します。
   * @param clickedPosition
   */
  createNodeAtClickedPosition(clickedPosition: Point): NodeId {
    // クリックした位置を中心としてノードを配置する
    const position = StickyNode.getPositionFromCenter(clickedPosition);
    return this.createNode(Point.fromPosition(position));
  }

  /**
   * 指定したキャンバス座標（ビュー内ローカル座標）にゾーンが存在する場合、そのゾーンのキーを返します。
   * 複数のゾーンが重なっている場合は最前面のゾーンを返します。
   * @param position ビュー内ローカル座標
   */
  findZoneAtPoint(position: Point): StickyZoneKey | null {
    const zone = this.model.findPasteTargetZone(position, this.displayOrderTree);
    return zone ? zone.key : null;
  }

  /**
   * ゾーン内のクリックされた位置にノードを追加します。同時にゾーンの親子関係も作ります。
   * @param clickedPosition
   * @param zoneKey
   */
  createNodeToZone(clickedPosition: Point, zoneKey: StickyZoneKey): NodeId {
    const nodeId = this.nodeCreationOperation.createNodeWithCenterPositionAndAddToZone(
      this.viewModel,
      clickedPosition,
      this.styleProvider.nodeStyle(),
      zoneKey
    );
    this.selectOnly(nodeId);

    return nodeId;
  }

  /**
   * 指定された位置にノードを作成し、そのノードを選択した上でそのIDを返します。
   * @param position
   */
  createNode(position: Point): NodeId {
    const nodeId = this.nodeCreationOperation.createNode(this.viewModel, position, this.styleProvider.nodeStyle());
    this.selectOnly(nodeId);

    return nodeId;
  }

  /**
   * 指定された色で指定された位置にノードを作成します。
   * 次回の新規作成にも同じ色が反映されます。
   * @param position
   * @param color
   */
  createNodeWithColor(position: Point, color: ThemeColor): NodeId {
    this.styleProvider.nodeColorChanged(color);
    return this.createNode(position);
  }

  /**
   * クリックした位置に指定された色のノードを作成します。
   * @param clickedPosition
   * @param color
   */
  createNodeAtClickedPositionWithColor(clickedPosition: Point, color: ThemeColor): NodeId {
    const position = StickyNode.getPositionFromCenter(clickedPosition);
    return this.createNodeWithColor(Point.fromPosition(position), color);
  }

  /**
   * ゾーン内のクリックされた位置に指定された色のノードを追加します。
   * @param clickedPosition
   * @param zoneKey
   * @param color
   */
  createNodeToZoneWithColor(clickedPosition: Point, zoneKey: StickyZoneKey, color: ThemeColor): NodeId {
    this.styleProvider.nodeColorChanged(color);
    const nodeId = this.nodeCreationOperation.createNodeWithCenterPositionAndAddToZone(
      this.viewModel,
      clickedPosition,
      this.styleProvider.nodeStyle(),
      zoneKey
    );
    this.selectOnly(nodeId);
    return nodeId;
  }

  /** リンクを張ったノードを作成する */
  createLinkedNode(position: Point, fromKey: LinkableTargetKey): [StickyNode, LinkEntity] {
    const [nodeCommand, node] = this.nodeCreationOperation.buildNodeCreateCommand(
      this.viewModel,
      position,
      this.styleProvider.nodeStyle()
    );
    const [linkCommand, link] = this.linkCreationOperation.buildLinkCreateCommand(
      fromKey,
      NodeKey.buildFromID(node.id),
      this.styleProvider.linkStyle()
    );
    this.commandManager.execute(new CompositeCommand(nodeCommand, linkCommand));
    this.selectOnly(node.id);

    return [node, link];
  }

  // ============================================================
  // 要素の選択状態
  // ============================================================

  /**
   * 全ての選択を解除する
   */
  deselectAll(): void {
    this.selectedItemsOperation.deselectAll(this.selectedItems);
    this.selectedItems = SelectedItemSet.from([]);
  }

  /**
   * 指定されたアイテムの選択状態を逆転する (選択済みであれば選択解除、未選択であれば選択する)
   * @param id
   */
  toggleSelection(id: ModelElementId): void {
    if (this.selectedItems.getSelectedItems().includes(id)) {
      this.selectedItemsOperation.deselect(id, this.selectedItems);
      this.selectedItems = this.selectedItems.deselect(id);
    } else {
      this.selectedItemsOperation.addToSelection(id, this.selectedItems);
      this.selectedItems = this.selectedItems.addToSelection(id);
    }
  }

  /**
   * 既存のアイテムの選択を全て解除して、渡されたアイテムを選択する
   * @param id
   */
  selectOnly(id: ModelElementId): void {
    this.selectedItemsOperation.selectOnly(id, this.selectedItems);
    this.selectedItems = SelectedItemSet.from([id]);
  }

  /**
   * アイテムを追加で選択する
   * @param id
   */
  addToSelection(id: ModelElementId): void {
    this.selectedItemsOperation.addToSelection(id, this.selectedItems);
    this.selectedItems = this.selectedItems.addToSelection(id);
  }

  /**
   * 複数のアイテムを追加で選択する
   * @param ids
   */
  addToSelectionMany(ids: ModelElementId[]): void {
    this.selectedItemsOperation.addToSelectionMany(ids, this.selectedItems);
    this.selectedItems = this.selectedItems.addToSelectionMany(ids);
  }

  /**
   * 全てのアイテムを選択する
   */
  selectAll(): void {
    const panelIds = this.panelContents.panels.allIds();
    const threadIds = this.commentContents.allIds();
    const fileContainerIds = this.fileContainerContents.allIds();
    const annotationLinkIds = this.annotationLinks.allIds();
    this.addToSelectionMany([
      ...this.model.elementIds(),
      ...threadIds,
      ...panelIds,
      ...fileContainerIds,
      ...annotationLinkIds,
    ]);
    this.selectedItems = SelectedItemSet.from([
      ...this.model.elementIds(),
      ...threadIds,
      ...panelIds,
      ...fileContainerIds,
      ...annotationLinkIds,
    ]);
  }

  /**
   * ドラッグ開始時に要素の選択状態を適切に変更する。
   *
   *  - ドラッグの対象要素が選択済みならば、既存の選択状態を変更しない
   *  - 対象要素が非選択状態のとき、
   *     - 複数選択モードであれば、対象要素を追加選択
   *     - そうでなければ、対象要素のみを単一選択
   *
   * @param id ドラッグ移動で掴んでいる対象要素の識別子
   * @param multiSelectionMode 複数選択モード
   */
  selectForDragStart(id: ModelElementId, isMultiSelected: boolean): void {
    if (this.selectedItems.getSelectedItems().includes(id)) {
      return;
    }

    if (isMultiSelected) {
      this.addToSelection(id);
    } else {
      this.selectOnly(id);
    }
  }

  // ============================================================
  // 選択中要素の操作
  // ============================================================
  private selectedNodes(): NodeCollection {
    return this.model.getNodes(this.selectedItems.getSelectedItems());
  }

  private selectedZones(): StickyZoneCollection {
    return this.model.getZones(this.selectedItems.getSelectedItems());
  }

  private selectedZonePositions(): PositionSet {
    return this.model.getZonePositions(this.selectedItems.getSelectedItems());
  }

  private selectedLinks(): LinkCollection {
    return this.model.getLinks(this.selectedItems.getSelectedItems());
  }

  private selectedNodeDescriptions(): ElementDescriptionSet {
    return this.nodeDescriptionSet.filterByModelElementIds(this.selectedItems.getSelectedItems());
  }

  private selectedZoneDescriptions(): ElementDescriptionSet {
    return this.zoneDescriptionSet.filterByModelElementIds(this.selectedItems.getSelectedItems());
  }

  private relatedNodeDescriptions(ids: NodeId[]): ElementDescriptionSet {
    return this.nodeDescriptionSet.filterByModelElementIds(ids);
  }

  private relatedZoneDescriptions(ids: StickyZoneId[]): ElementDescriptionSet {
    return this.zoneDescriptionSet.filterByModelElementIds(ids);
  }

  changeSelectedNodeZoneThemeColor(themeColor: ThemeColor): void {
    const command = CompositeCommand.composeOptionalCommands(
      this.selectedNodeThemeColorUpdateCommand(themeColor),
      this.selectedZoneThemeColorUpdateCommand(themeColor)
    );

    if (command) {
      this.commandManager.execute(command);

      // 変更された要素の種類に応じて最終変更情報を更新する
      if (!this.selectedNodes().isEmpty()) this.styleProvider.nodeColorChanged(themeColor);
      if (!this.selectedZones().isEmpty()) this.styleProvider.zoneColorChanged(themeColor);
    }
  }

  private selectedNodeThemeColorUpdateCommand(themeColor: ThemeColor): NodeStyleUpdateMultiCommand | undefined {
    const styleSet = this.selectedNodes().styleSet();
    return this.nodeStylesUpdateCommand(styleSet, styleSet.withThemeColor(themeColor));
  }

  private selectedZoneThemeColorUpdateCommand(themeColor: ThemeColor): StickyZoneStyleUpdateMultiCommand | undefined {
    const styleSet = this.selectedZones().styleSet();
    return this.zoneStylesUpdateCommand(styleSet, styleSet.withThemeColor(themeColor));
  }

  changeSelectedNodeZoneFontSize(fontSize: NodeFontSize): void {
    const command = CompositeCommand.composeOptionalCommands(
      this.selectedNodeFontSizeUpdateCommand(fontSize),
      this.selectedZoneFontSizeUpdateCommand(fontSize)
    );

    if (command) this.commandManager.execute(command);
  }

  toggleSelectedNodeFontStrikethrough(fontStrikethrough: boolean): void {
    const command = CompositeCommand.composeOptionalCommands(
      this.selectedNodeFontStrikethroughUpdateCommand(fontStrikethrough)
    );

    if (command) this.commandManager.execute(command);
  }

  changeSelectedNodeZoneTextAlign(textAlign: TextAlign): void {
    const command = CompositeCommand.composeOptionalCommands(
      this.selectedNodeTextAlignUpdateCommand(textAlign),
      this.selectedZoneTextAlignUpdateCommand(textAlign)
    );

    if (command) this.commandManager.execute(command);
  }

  changeSelectedZoneShape(shape: StickyZoneShape): void {
    const command = this.selectedZoneShapeUpdateCommand(shape);
    if (command) {
      this.commandManager.execute(command);
      this.styleProvider.zoneShapeChanged(shape);
    }
  }

  changeSelectedZoneTitlePosition(titlePosition: StickyZoneTitlePosition | null): void {
    const command = this.selectedZoneTitlePositionUpdateCommand(titlePosition);
    if (command) {
      this.commandManager.execute(command);
      this.styleProvider.zoneTitlePositionChanged(titlePosition);
    }
  }

  private selectedNodeTextAlignUpdateCommand(textAlign: TextAlign): NodeStyleUpdateMultiCommand | undefined {
    const styleSet = this.selectedNodes().styleSet();
    return this.nodeStylesUpdateCommand(styleSet, styleSet.withTextAlign(textAlign));
  }

  private selectedZoneTextAlignUpdateCommand(textAlign: TextAlign): StickyZoneStyleUpdateMultiCommand | undefined {
    const styleSet = this.selectedZones().styleSet();
    return this.zoneStylesUpdateCommand(styleSet, styleSet.withTextAlign(textAlign));
  }

  private selectedNodeFontStrikethroughUpdateCommand(
    fontStrikethrough: boolean
  ): NodeStyleUpdateMultiCommand | undefined {
    const styleSet = this.selectedNodes().styleSet();
    return this.nodeStylesUpdateCommand(styleSet, styleSet.withFontStrikethrough(fontStrikethrough));
  }

  private selectedNodeFontSizeUpdateCommand(fontSize: NodeFontSize): NodeStyleUpdateMultiCommand | undefined {
    const styleSet = this.selectedNodes().styleSet();
    return this.nodeStylesUpdateCommand(styleSet, styleSet.withFontSize(fontSize));
  }

  private selectedZoneFontSizeUpdateCommand(fontSize: FontSize): StickyZoneStyleUpdateMultiCommand | undefined {
    const styleSet = this.selectedZones().styleSet();
    return this.zoneStylesUpdateCommand(styleSet, styleSet.withFontSize(fontSize));
  }

  private selectedZoneShapeUpdateCommand(shape: StickyZoneShape): StickyZoneStyleUpdateMultiCommand | undefined {
    const styleSet = this.selectedZones().styleSet();
    return this.zoneStylesUpdateCommand(styleSet, styleSet.withShape(shape));
  }

  private selectedZoneTitlePositionUpdateCommand(
    titlePosition: StickyZoneTitlePosition | null
  ): StickyZoneStyleUpdateMultiCommand | undefined {
    const styleSet = this.selectedZones().styleSet();
    return this.zoneStylesUpdateCommand(styleSet, styleSet.withTitlePosition(titlePosition));
  }

  private nodeStylesUpdateCommand(from: NodeStyleSet, to: NodeStyleSet): NodeStyleUpdateMultiCommand | undefined {
    if (from.isEqual(to)) return;

    return new NodeStyleUpdateMultiCommand(from, to, this.nodeRepository);
  }

  private zoneStylesUpdateCommand(
    from: StickyZoneStyleSet,
    to: StickyZoneStyleSet
  ): StickyZoneStyleUpdateMultiCommand | undefined {
    if (from.isEqual(to)) return;

    return new StickyZoneStyleUpdateMultiCommand(from, to, this.zoneRepository);
  }

  changeSelectedLinkLineStyles(lineStyle: LinkLineStyle): void {
    const styleSet = this.selectedLinks().styleSet();
    this.changeLinkStyles(styleSet, styleSet.withLineStyle(lineStyle));
    this.styleProvider.linkLineStyleChanged(lineStyle);
  }

  changeSelectedLinkMarkStyles(markStyle: LinkMarkStyle): void {
    const styleSet = this.selectedLinks().styleSet();
    this.changeLinkStyles(styleSet, styleSet.withMarkStyle(markStyle));
    this.styleProvider.linkMarkStyleChanged(markStyle);
  }

  changeReverseLinks(): void {
    const links = this.selectedLinks().entities();
    const command = new CompositeCommand(
      ...links.map((link) => {
        return new LinkReverseCommand(this.viewModel, this.linkEntityOperation, link);
      })
    );
    this.commandManager.execute(command);
  }

  changeSelectedLinkColors(color: LinkColor): void {
    const styleSet = this.selectedLinks().styleSet();
    this.changeLinkStyles(styleSet, styleSet.withColor(color));
    this.styleProvider.linkColorChanged(color);
  }

  private changeLinkStyles(from: LinkStyleSet, to: LinkStyleSet): void {
    if (from.isEqual(to)) return;

    const command = new LinkStyleUpdateMultiCommand(from, to, this.linkRepository);
    this.commandManager.execute(command);
  }

  /**
   * 選択されているノードとゾーンをグルーピング（ゾーンを作成してその中に追加）します。
   */
  async groupSelectedElements(): Promise<string | undefined> {
    const zoneId = await this.zoneCreationOperation.createZoneFor(
      this.selectedNodes(),
      this.selectedZones(),
      this.selectedZonePositions(),
      this.styleProvider.zoneStyle()
    );

    if (!zoneId) return;

    this.selectOnly(zoneId);

    return zoneId;
  }

  /**
   * 選択中の要素からLinkだけ削除する
   */
  removeMultiSelectedLinks(): void {
    const selectedLinks = this.selectedLinks().entities();
    const command = new LinkMultiDeleteCommand(this.viewModel, this.linkEntityOperation, selectedLinks);
    this.commandManager.execute(command);
  }

  /**
   * 選択中のNodeを整列させる
   */
  alignSelectedNodes(alignType: AlignType, interval: number): void {
    const targetNodes = this.selectedNodes().entities();
    // 左端・上端のノードを基準にする
    targetNodes.sort(StickyNode.compareByPositionXY);
    const refNode = targetNodes[0];
    const nodePositionDiff =
      (alignType === 'vertical' ? refNode.size().height : refNode.size().width) + ModelLayout.GridSize * interval;
    const newNodes: StickyNode[] = [];

    targetNodes.forEach((node, index) => {
      if (alignType === 'vertical') {
        newNodes.push(
          node.withPosition(
            new NodePosition({ x: refNode.position.x, y: refNode.position.y + nodePositionDiff * index })
          )
        );
      } else {
        newNodes.push(
          node.withPosition(
            new NodePosition({ x: refNode.position.x + nodePositionDiff * index, y: refNode.position.y })
          )
        );
      }
    });

    const command = new NodeCollectionUpdateCommand(
      this.viewModel,
      this.nodeEntityOperation,
      this.selectedNodes(),
      new NodeCollection(newNodes)
    );
    this.commandManager.execute(command);
  }

  // ============================================================
  // モデルコメントの操作
  // ============================================================

  /**
   * モデルコメント・スレッドの作成
   * @param creatingModelComment
   */
  async createModelCommentThread(creatingModelComment: CreatingModelComment): Promise<void> {
    await this.modelCommentOperation.createThread(creatingModelComment);
  }

  /**
   * 現在選択中ノードのドロップ先ゾーンを探す
   */
  findDropTargetZones(): StickyZone[] {
    const ids = this.getDroppableSelectedIdsIntoZone();
    if (ids.length === 0) return [];

    return this.model.findDropTargetZones(ids, this.displayOrderTree);
  }

  findDropTargetZonesByRect(droppingRect: Rect): StickyZone[] {
    return this.model.findDropTargetZonesByRect(droppingRect, this.displayOrderTree);
  }

  findElementsIncludedByDroppingRect(droppingRect: Rect): (StickyZoneId | NodeId)[] {
    return this.model.findElementsIncludedByDroppingRect(droppingRect, this.displayOrderTree);
  }

  // 選択中の要素をドロップしたときの矩形を返す
  getDroppingRect(): Rect | null {
    const selectedIds = this.getDroppableSelectedIdsIntoZone();
    const droppingRect = this.model.getBoundsOf(selectedIds);
    return droppingRect;
  }

  // ゾーンに対してドロップ可能な選択中要素IDのリストを返す
  getDroppableSelectedIdsIntoZone(): ModelElementId[] {
    return [...this.selectedNodes().ids(), ...this.selectedZones().ids()];
  }

  private draggingElements(): DraggingElements | null {
    const ids = this.getDroppableSelectedIdsIntoZone();
    if (ids.length === 0) return null;

    const { displayOrderTree } = this;

    const draggingElementIds = displayOrderTree.minimizeElementIds(ids);
    const dropTargetZones = this.model.findDropTargetZones(draggingElementIds, displayOrderTree);

    return new DraggingElements(
      draggingElementIds,
      dropTargetZones.map((zone) => zone.id)
    );
  }

  /**
   * 選択要素をドラッグ＆ドロップしたものとして、親子関係変更のコマンドを生成します。
   */
  async buildSelectedElementsDropCommand(): Promise<ICommand | null> {
    const draggingElements = this.draggingElements();
    if (!draggingElements || !(await draggingElements.willChangeParents(this.displayOrderRepository))) return null;

    // 親子関係ができる場合はリンクを削除する
    const links = this.model.aggregateRemovingLinksBy(draggingElements, this.displayOrderTree);

    return CompositeCommand.composeOptionalCommands(
      draggingElements.buildChangeParentCommand(this.displayOrderRepository),
      ...links.deleteCommands(this.viewModel, this.linkEntityOperation)
    );
  }

  selectedModelElementPositionMap(): ModelElementPositionMap {
    const { displayOrderTree } = this;

    const ids = displayOrderTree.aggregateDescendantIds(this.selectedItems.getSelectedItems());

    const zonePositions = this.model.getZonePositions(ids);
    const nodes = this.model.getNodes(ids);

    return new ModelElementPositionMap(nodes.positionSet(), zonePositions);
  }

  /**
   * 選択中の要素をクリップボードにコピーし、コピーが成功したかどうかを返します。
   */
  async copySelectedElements(): Promise<boolean> {
    const { contents } = this.buildSelectedContents();

    return this.copyContents(contents);
  }

  /**
   * 選択中の要素と、選択中のゾーンに含まれる要素を返す
   */
  getSelectedElementsIncludeZoneContents(): StickyModelContents {
    const { contents } = this.buildSelectedContents({ includeZoneContents: true });
    return contents;
  }

  getSelectedTopZone(): { topZone?: StickyZone; topZonePosition?: Point } {
    const { contents } = this.buildSelectedContents({ includeZoneContents: true });

    const top = contents.getTopZone();
    const topZonePosition = top ? contents.zonePositionSet().find(top.id) : undefined;

    const topZone = top ? contents.zoneEntities().find((zone) => zone.id === top.id) : undefined;

    return {
      topZone,
      topZonePosition,
    };
  }

  /**
   * 選択中のZoneの子要素のみのコンテンツを作成
   */
  buildSelectedContentsWithoutTopZone(topZoneId: string): StickyModelContents {
    const { model, displayOrderTree } = this;

    const descendantIds = displayOrderTree.aggregateDescendantIds([topZoneId]);

    const nodes = this.model.getNodes(descendantIds);

    // Zoneは自分自身を含まないようにする
    const zones = this.model.getZones(descendantIds).filter((zone) => zone.id !== topZoneId);
    const zonePositions = this.model.getZonePositions(descendantIds).remove(topZoneId);

    const nodeDescriptions = this.relatedNodeDescriptions(nodes.ids());
    const zoneDescriptions = this.relatedZoneDescriptions(zones.ids());

    const links = this.model.getAllLinks();

    const result = StickyModelContents.fromFragments(
      model.key,
      model.version,
      nodes,
      links,
      zones,
      zonePositions,
      ModelCommentContents.buildEmpty(model.key),
      DescriptionPanelCollectionContents.buildEmpty(),
      // ファイルコンテナは説明パネルと同じくゾーンに属さない（DisplayOrderTree に入れない）ため、
      // ゾーンをビューに変換しても移らない。ゾーンを動かしても付いてこないのと同じ扱いとして受容している。
      FileContainerCollectionContents.buildEmpty(),
      AnnotationLinkCollection.buildEmpty(),
      nodeDescriptions,
      zoneDescriptions,
      displayOrderTree
    );

    return result.contents;
  }

  private async copyContents(contents: StickyModelContents): Promise<boolean> {
    if (contents.isEmpty()) {
      return false;
    }

    const payload = StickyModelClipboardPayload.fromContent(this.viewModel.id, contents);
    const json = JSON.stringify(payload);
    const text = contents.exportNodesText();

    try {
      await ClipboardUtils.copyWithCustomFormat({ json, text });
      return true;
    } catch (e) {
      console.warn('クリップボードへのコピーに失敗しました', e);
      return false;
    }
  }

  /**
   * クリープボードの内容をビューに貼り付けます。
   * @return エラーの場合はエラー文字列。成功またはモデル内のコンテンツ形式ではないため貼り付けしなかった場合はnullを返します。
   */
  async pasteClipboardPayload(
    payload: ApplicationClipboardPayload,
    position: Point,
    onFileContainerCreated?: (id: FileContainerId) => void
  ): Promise<string | null> {
    if (isStickyModelClipboardPayload(payload)) return this.pasteStickyModelClipboardPayload(payload, position);
    if (isTSVClipboardPayload(payload)) return this.pasteTSV(payload, position);
    if (isTextClipboardPayload(payload)) return this.pasteText(payload, position);
    if (isFileClipboardPayload(payload) || isImageClipboardPayload(payload)) {
      return this.pasteFiles(payload, position, onFileContainerCreated);
    }

    // ビューの貼り付けなどここで対応しないpayloadが渡ってきた場合はエラーではなく無視する
    // （別の場所でハンドルされるためエラーメッセージが出るとおかしい）
    return null;
  }

  /**
   * 同じモデルからの貼り付けかどうかや同じクリップボードからの貼り付け回数に応じて位置を調整して貼り付けます。
   */
  private async pasteStickyModelClipboardPayload(
    payload: StickyModelClipboardPayload,
    position: Point
  ): Promise<string | null> {
    const contents = StickyModelClipboardPayload.toContent(payload);

    const fileAssetClone = await FileContainerAssetCloner.clone(
      payload.sourceViewModelId,
      { groupId: this.groupId, viewModelId: this.viewModel.id },
      contents.fileContainerAssetIds(),
      this.currentUserId
    );
    if (!fileAssetClone.ok) return fileAssetClone.errorMessage;

    // 復元したペイロード中に含まれる ViewModelAssets の情報を取得して複製する
    const assets = await ViewModelAssetCollectionForClone.fetchByUrlList(contents.assetUrls());
    const baseUrl = location.href;
    const [assetUrlMap, hasError] = await assets.cloneNew(this.groupId, this.viewModel.id, this.currentUserId, baseUrl);
    const errorMessage = hasError ? t('canvas.asset.cloneFailed') : null;

    // ビューやビューモデルのように完成したモデルそのものを貼り付けるのと違い、自分の付箋・ゾーンとして貼り付ける。
    // 参考: https://github.com/levii/balus-app/pull/991#issuecomment-908317310
    const adjustedModelContents = this.pastePositionAdjuster.adjustElements(
      contents
        .cloneNew(assetUrlMap, fileAssetClone.assetCopy)
        .updateNodeZoneCreatedUserKey(UserKey.buildFromId(this.currentUserId)),
      position,
      this.buildSelectedContents().contents.getUnionRect()
    );
    const pasteTargetZoneId = this.model.findPasteTargetZone(position, this.displayOrderTree)?.id;

    this.deselectAll();
    this.createElements(adjustedModelContents, pasteTargetZoneId ? [pasteTargetZoneId] : undefined);
    this.addToSelectionMany(adjustedModelContents.elementIds());

    return errorMessage;
  }

  private async pasteFiles(
    payload: FileClipboardPayload | ImageClipboardPayload,
    position: Point,
    onFileContainerCreated?: (id: FileContainerId) => void
  ): Promise<string | null> {
    return this.uploadAndAddFileContainers(payload.files, position, onFileContainerCreated);
  }

  private pasteTSV(payload: TSVClipboardPayload, position: Point): string | null {
    const table = new FixedCellSizeTextTable(payload.data, StickyNode.size(), ModelLayout.GridSize);
    if (table.cellCount() > MaxPasteTSVCount) {
      return t('canvas.clipboard.error.pasteLimitExceeded', { maxCount: MaxPasteTSVCount });
    }

    const nodes = this.nodeCreationOperation.createNodesFromTable(
      this.viewModel,
      table,
      position,
      this.styleProvider.nodeStyle()
    );

    this.deselectAll();
    this.addToSelectionMany(nodes.ids());
    return null;
  }

  private pasteText(payload: TextClipboardPayload, position: Point): string | null {
    const text = payload.data;
    if (text.length > MaxPasteTextLength) {
      return t('canvas.clipboard.error.textLimitExceeded', { maxLength: MaxPasteTextLength });
    }

    const isUrlString = (url: string): boolean => {
      try {
        new URL(url); // URLとして解釈できない文字列が渡された場合には例外を投げる
        return true;
      } catch {
        return false;
      }
    };

    const nodeId = this.nodeCreationOperation.createNode(
      this.viewModel,
      position,
      this.styleProvider.nodeStyle(),
      text,
      isUrlString(text) ? text : null
    );

    this.deselectAll();
    this.selectOnly(nodeId);
    return null;
  }

  /**
   * 選択中の要素を複製して貼り付けます。
   */
  async duplicateSelectedElements(position: Point): Promise<string | null> {
    const { contents } = this.buildSelectedContents();
    if (contents.isEmpty()) return null;

    const payload = StickyModelClipboardPayload.fromContent(this.viewModel.id, contents);

    return this.pasteStickyModelClipboardPayload(payload, position);
  }

  /**
   * 指定した位置に説明パネルを追加し、追加した説明パネルを選択します。
   */
  addDescriptionPanel(position: Point): DescriptionPanelId | undefined {
    const panelId = DescriptionPanelCreationOperation.create({
      position,
      viewModelId: this.viewModel.id,
      modelId: this.model.id,
      commandManager: this.commandManager,
    });
    if (!panelId) return undefined;
    this.selectOnly(panelId);
    return panelId;
  }

  addFileContainer(
    position: Point,
    attrs: Omit<Parameters<typeof FileContainerCreationOperation.create>[0]['attrs'], 'createdUserId'>
  ): FileContainerId {
    const id = FileContainerCreationOperation.create({
      groupId: this.groupId,
      position,
      attrs: { ...attrs, createdUserId: this.currentUserId },
      viewModelId: this.viewModel.id,
      modelId: this.model.id,
      commandManager: this.commandManager,
    });
    this.selectOnly(id);
    return id;
  }

  async uploadAndAddFileContainers(
    files: File[],
    position: Point,
    onCreated?: (id: FileContainerId) => void
  ): Promise<string | null> {
    if (files.length > MaxUploadFileCount) {
      return t('canvas.fileContainer.upload.countLimit.message', { max: MaxUploadFileCount });
    }
    for (const file of files) {
      const invalid = ViewModelFileAssetUploader.validate(file);
      if (invalid?.reason === 'emptyFile') {
        return t('canvas.fileContainer.upload.emptyFile.message', { filename: invalid.filename });
      }
      if (invalid?.reason === 'fileSizeLimit') {
        return t('canvas.asset.fileSizeLimit', { maxFileSizeMB: MaxFileAssetSizeInMB, filename: invalid.filename });
      }
    }

    const stickySize = StickyNode.size();
    const imageSizes: (Size | null)[] = [];
    // 画像を同時にデコードするとメモリを大きく使うため、1 件ずつ測る
    for (const file of files) imageSizes.push(await this.measureImageSize(file));
    // 画像かどうかはサーバーが拡張子から決め、ブラウザの判定と一致するとは限らない（BMP など）。
    // 画像として測れたファイルは、画像・非画像のどちらの寸法になっても後続のカードと重ならない大きさで並べる。
    const layoutSizes = imageSizes.map((imageSize) =>
      imageSize
        ? new Size(Math.max(imageSize.width, stickySize.width), Math.max(imageSize.height, stickySize.height))
        : stickySize
    );
    const positions = layoutFileContainers(position, layoutSizes);
    const placeholders: UploadingFileContainer[] = files.map((file, index) => ({
      id: newFileContainerId(),
      filename: file.name,
      position: positions[index],
      size: imageSizes[index] ?? stickySize,
    }));
    this.updateUploadingFileContainers((current) => [...current, ...placeholders]);
    const removePlaceholders = (targets: UploadingFileContainer[]) => {
      const ids = new Set(targets.map(({ id }) => id));
      this.updateUploadingFileContainers((current) => current.filter(({ id }) => !ids.has(id)));
    };

    try {
      const limitGB = ViewModelFileStorageLimitBytes / 1024 ** 3;
      const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
      const capacity = await ViewModelFileAssetUploader.fitsCapacity(this.groupId, this.viewModel.id, totalBytes);
      if (!capacity.ok || !capacity.fits) {
        if (!capacity.ok) {
          if (capacity.reason === 'forbidden') return t('common.label.licenseRequired');
          if (capacity.reason === 'signInRequired') return t('canvas.fileContainer.signInRequired');
          return t('canvas.fileContainer.upload.failed.message', {
            filename: files.map((file) => file.name).join(', '),
          });
        }
        return t('canvas.fileContainer.upload.overLimit.message', {
          filename: files.map((file) => file.name).join(', '),
          limitGB,
        });
      }

      const failedFilenames: string[] = [];
      let createdCount = 0;
      for (const [index, file] of files.entries()) {
        const placeholder = placeholders[index];
        const result = await ViewModelFileAssetUploader.upload(this.groupId, this.viewModel.id, file);
        if (!result.ok) {
          if (result.reason === 'forbidden' || result.reason === 'overLimit') {
            return result.reason === 'forbidden'
              ? t('common.label.licenseRequired')
              : t('canvas.fileContainer.upload.overLimit.message', { filename: file.name, limitGB });
          }
          removePlaceholders([placeholder]);
          failedFilenames.push(file.name);
          continue;
        }

        const { assetId, filename, fileSize, contentType } = result.asset;
        const size = contentType.startsWith('image/') ? (imageSizes[index] ?? stickySize) : stickySize;
        // CreateCommand は RTDB への保存結果を待たないため、ここで書き込み失敗を検知して実体を戻すことはできない。
        // 他の要素の作成と同じ仕様として受容している。編集権限はサーバーが確認済みで、失敗は稀。
        const id = this.addFileContainer(placeholder.position, { assetId, filename, fileSize, contentType, size });
        removePlaceholders([placeholder]);
        onCreated?.(id);
        createdCount += 1;
      }

      if (createdCount > 0) {
        toast.success(t('canvas.fileContainer.upload.success.message'));
      }
      return failedFilenames.length > 0
        ? t('canvas.fileContainer.upload.failed.message', { filename: failedFilenames.join(', ') })
        : null;
    } finally {
      removePlaceholders(placeholders);
    }
  }

  private updateUploadingFileContainers(update: (current: UploadingFileContainer[]) => UploadingFileContainer[]): void {
    const uploadingAtom = uploadingFileContainersAtomFamily(this.model.id);
    this.store.set(uploadingAtom, update(this.store.get(uploadingAtom)));
  }

  resizeFileContainer(id: FileContainerId, { dx, dy }: PositionDelta, size: Size): void {
    const { containers, positions } = this.fileContainerContents;
    const container = containers.get(id);
    const position = positions.find(id);
    if (!container || !position || (dx === 0 && dy === 0 && container.size.isEqual(size))) return;

    const viewModelId = this.viewModel.id;
    const modelId = this.model.id;
    this.commandManager.execute(
      new CompositeCommand(
        new UpdateCommand(
          position,
          position.addXY(dx, dy),
          new ObjectRepository(Point, RTDBPath.FileContainer.positionPath(viewModelId, modelId, id))
        ),
        new UpdateCommand(
          container.size,
          size,
          new ObjectRepository(Size, RTDBPath.FileContainer.sizePath(viewModelId, modelId, id))
        )
      )
    );
  }

  async deleteFileContainer(id: FileContainerId): Promise<boolean> {
    const fileContainers = this.fileContainerContents.filterByIds([id]);
    const fileContainerDeleteCommand = await this.buildFileContainersDeleteCommand(fileContainers);
    if (!fileContainerDeleteCommand) return false;

    const annotationLinkDeleteCommand = await this.annotationLinks
      .filterConnectedTo([id], [])
      .buildDeleteCommand(this.viewModel.id, this.model.id);
    const command = annotationLinkDeleteCommand
      ? new CompositeCommand(fileContainerDeleteCommand, annotationLinkDeleteCommand)
      : fileContainerDeleteCommand;

    const deleted = await this.executeWithFileAssetTrash(fileContainers.assetIds(), command);
    if (deleted) this.deselectAll();
    return deleted;
  }

  private executeWithFileAssetTrash(assetIds: ViewModelAssetId[], command: ICommand): Promise<boolean> {
    return FileContainerAssetTrashCommand.executeDeletion(
      this.commandManager,
      { groupId: this.groupId, viewModelId: this.viewModel.id },
      assetIds,
      command
    );
  }

  private async buildFileContainersDeleteCommand(
    fileContainers: FileContainerCollectionContents
  ): Promise<ICommand | null> {
    const positionSet = await this.fileContainerPositionSetRepository.getMany(fileContainers.allIds());
    return fileContainers.buildDeleteCommand(this.viewModel.id, this.model.id, positionSet);
  }

  private async measureImageSize(file: File): Promise<Size | null> {
    try {
      const bitmap = await createImageBitmap(file);
      const size = fitImageSize(bitmap.width, bitmap.height, StickyNode.size());
      bitmap.close();
      return size;
    } catch {
      return null;
    }
  }

  /**
   * クリックした位置に説明パネルを作成します。
   * クリック位置がパネルの中心になるよう左上座標に変換します。
   * @param clickedPosition
   */
  addDescriptionPanelAtClickedPosition(clickedPosition: Point): DescriptionPanelId | undefined {
    const { width, height } = DescriptionPanel.DefaultSize;
    const position = clickedPosition.addXY(-width / 2, -height / 2);
    return this.addDescriptionPanel(position);
  }

  getCreatePoint(transformedVisibleAreaCenterPoint: Point): Point {
    const selectedRect = this.buildSelectedContents().contents.getUnionRect();
    const creatingContentBaseTopLeftPoint = selectedRect ? selectedRect.topLeft() : transformedVisibleAreaCenterPoint;
    return creatingContentBaseTopLeftPoint.addXY(StickyNode.size().width / 2, StickyNode.size().height / 2);
  }
}
